import type { FleetComponentRow, Json } from '../../lib/database.types'
import { arr, obj, str } from '../../lib/json'
import { humanise } from '../../lib/text'
import { age } from '../../lib/time'

/**
 * `public.fleet_components`, as the publisher actually writes it.
 *
 * This mapper computes nothing and infers nothing about a component. Every
 * fact on the page was written in a descriptor beside that component's code,
 * checked against the code by CI, and published by a timer on the box. All
 * that happens here is presentation: grouping, ordering, and turning a jsonb
 * array into something a list can render.
 *
 * Two things follow from that, and they are the two the view is most likely to
 * get wrong.
 *
 * Nothing names a `kind`. The groups, their headings and their order all come
 * out of the rows -- `kind`, `kind_label`, `kind_sort` -- so a kind nobody has
 * written yet gets its own heading in the right place with no code change,
 * exactly as a new department slug does. A `kind === 'script' ? ...` here is
 * product rule 2 dying one level down.
 *
 * And nothing switches on a known key set inside the jsonb. The objects are
 * open: `invocation` carries different keys for a systemd timer than for a
 * webhook, and a descriptor that grows a `region` tomorrow should reach the
 * page rather than vanish. So the scalars are harvested generically and
 * labelled from their own keys, the same way `readFacts` does in
 * `approvals.ts`. A key this app has never seen is shown, not dropped.
 *
 * The one deliberate exception is `secrets`, and it is a refusal rather than a
 * shortcut: see `readSecrets`.
 */

/** A scalar the descriptor carried, labelled from its own key. */
export interface Fact {
  label: string
  value: string
}

/**
 * One thing a component touches: a table it reads, a file it writes, a comment
 * it posts. The fields named here are the ones the page is *about*; everything
 * else the entry carried lands in `facts`.
 */
export interface Interaction {
  /** What it talks to, as the descriptor named it: `system`, or `channel`. */
  system: string | null
  /** The specific thing touched, or for a send, who receives it. */
  target: string | null
  /** Named tables, when the entry listed them instead of a single target. */
  tables: string[]
  /** The descriptor's own prose about this one interaction. */
  what: string | null
  /** Writes only. 'agent' and 'human' are different facts -- see below. */
  actor: string | null
  /**
   * How to say that actor beside the entry. The Equip planner reads; its
   * documented monthly procedure has a person running an `update`. Rendering
   * those two identically is wrong in either direction, so the distinction is
   * carried here rather than left to the view to guess at.
   *
   * An actor this app does not recognise is shown verbatim -- the honest
   * answer for a word somebody else chose.
   */
  actorLabel: string | null
  /** Whether a person performs it: the view's cue to set it apart. */
  byHuman: boolean
  /** Everything else the entry carried, harvested rather than named. */
  facts: Fact[]
}

export type TouchKind = 'reads' | 'writes' | 'sends'

/**
 * Reads, Writes and Sends, always three lists and never merged into one. The
 * difference between them is the entire point of the page: someone taking this
 * over should be able to answer "can this thing email a customer" without
 * reading Python, and a single "touches" list cannot answer it.
 */
export interface TouchList {
  key: TouchKind
  label: string
  entries: Interaction[]
  /**
   * What an empty list means, said in words. Blank is not an answer: the page
   * has to distinguish "changes nothing outside itself" from "nobody wrote it
   * down", and a missing section reads as the second.
   */
  empty: string
}

export interface Invocation {
  /** systemd / person / manual / webhook -- whatever the descriptor said. */
  type: string | null
  /** Everything else it carried: the cadence, the unit, the user it runs as. */
  facts: Fact[]
  /**
   * Where its credentials come from, or null when the object carries no
   * `credentials` key at all. The absence is a fact about the component -- the
   * scratch sweep is given none -- so the view states it rather than rendering
   * an empty field.
   */
  credentials: Fact[] | null
}

/** A secret's name and where it comes from. Never a value: see `readSecrets`. */
export interface Secret {
  name: string
  from: string | null
}

export interface Component {
  slug: string
  name: string
  kind: string
  kindLabel: string
  status: string
  summary: string
  /** Markdown. The view parses it to text nodes; nothing renders it as markup. */
  description: string | null
  maintainer: string | null
  model: string | null
  repo: string | null
  path: string | null
  docUrl: string | null
  invocation: Invocation
  touches: TouchList[]
  secrets: Secret[]
  /** The open column: anything the descriptor carried that has no home above. */
  extra: Fact[]
  recordsRuns: boolean
  /**
   * Whether it appears in Lanes and reaches Costs, in words. Two of the five
   * agents record no run row -- they run in an interactive session, because
   * the Supabase connector only works in one -- and nothing else in this app
   * explains why they are missing from two views.
   */
  recordsRunsNote: string
}

export interface ComponentGroup {
  kind: string
  /** The row's own `kind_label`. Nothing here holds a table of names. */
  label: string
  components: Component[]
}

/**
 * How fresh the page is. A directory that has quietly stopped being
 * republished looks exactly like a fleet that has stopped changing, and the
 * second is a much nicer thing to believe.
 */
export interface Freshness {
  sentence: string
  generatedAt: string | null
  /** Short enough to read out, long enough to find. */
  commit: string | null
}

/** A scalar as text, or null if it is not one. Arrays of scalars are joined. */
function scalar(value: Json | undefined): string | null {
  if (typeof value === 'string') return value.trim() ? value : null
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (Array.isArray(value)) {
    // `writable_paths` is a list of strings, and skipping arrays the way a
    // scalars-only harvest would means losing the answer to what a unit is
    // allowed to write to.
    const parts = value
      .map((entry) => (entry !== null && typeof entry === 'object' ? null : scalar(entry)))
      .filter((part): part is string => part !== null)
    return parts.length > 0 ? parts.join(', ') : null
  }
  return null
}

const NOTHING_SKIPPED: ReadonlySet<string> = new Set<string>()

/**
 * Every scalar an open object carried, labelled from its own key.
 *
 * `skip` holds the keys the caller has already given a place of their own, so
 * they are not said twice. Everything else arrives here whether this app has
 * heard of it or not, which is the whole point.
 */
export function factsFrom(value: Json | undefined, skip: ReadonlySet<string> = NOTHING_SKIPPED): Fact[] {
  const root = obj(value)
  if (!root) return []
  const facts: Fact[] = []
  for (const [key, entry] of Object.entries(root)) {
    if (skip.has(key)) continue
    const text = scalar(entry)
    if (text !== null) facts.push({ label: humanise(key), value: text })
  }
  return facts
}

/** The keys an interaction gives a place of its own. The rest become facts. */
const NAMED = new Set(['system', 'channel', 'target', 'to', 'tables', 'what', 'actor'])

function actorLabel(actor: string | null): string | null {
  if (actor === null) return null
  if (actor === 'human') return 'By a person, by hand'
  if (actor === 'agent') return 'By the component'
  return actor
}

function toInteraction(value: Json): Interaction {
  const entry = obj(value)
  if (!entry) {
    // A bare string in the array rather than an object. Still says something.
    const text = scalar(value)
    return {
      system: null, target: text, tables: [], what: null,
      actor: null, actorLabel: null, byHuman: false, facts: [],
    }
  }
  const actor = str(entry['actor'])
  return {
    system: str(entry['system']) ?? str(entry['channel']),
    target: str(entry['target']) ?? str(entry['to']),
    tables: arr(entry['tables']).map((name) => str(name)).filter((name): name is string => name !== null),
    what: str(entry['what']),
    actor,
    actorLabel: actorLabel(actor),
    byHuman: actor === 'human',
    facts: factsFrom(value, NAMED),
  }
}

/** Reads, Writes and Sends, and what each one's absence means. */
const TOUCHES: ReadonlyArray<{ key: TouchKind; label: string; empty: string }> = [
  { key: 'reads', label: 'Reads', empty: 'Reads nothing outside itself.' },
  { key: 'writes', label: 'Writes', empty: 'Changes nothing outside itself.' },
  { key: 'sends', label: 'Sends', empty: 'Sends nothing anywhere.' },
]

/** The keys `invocation` gives a place of its own. */
const INVOCATION_NAMED = new Set(['type', 'credentials'])

function readInvocation(value: Json): Invocation {
  const root = obj(value)
  const credentials = root ? factsFrom(root['credentials']) : []
  return {
    type: root ? str(root['type']) : null,
    facts: factsFrom(value, INVOCATION_NAMED),
    // `credentials` absent and `credentials` empty are the same answer, and
    // both are said in words rather than shown as a gap.
    credentials: credentials.length > 0 ? credentials : null,
  }
}

/**
 * Names only.
 *
 * The column holds names by contract and the publisher refuses values, but
 * this does not assume that. It reads `name` and `from` and nothing else, so
 * a field added to a descriptor next year -- or a value pasted into one by
 * mistake -- cannot reach the screen through the generic harvest the rest of
 * this file uses. That is why `secrets` is the one place here that names its
 * keys, and it is a refusal rather than a shortcut.
 */
function readSecrets(value: Json): Secret[] {
  const out: Secret[] = []
  for (const row of arr(value)) {
    const entry = obj(row)
    const name = entry ? str(entry['name']) : str(row)
    if (!name) continue
    out.push({ name, from: entry ? str(entry['from']) : null })
  }
  return out
}

export function toComponent(row: FleetComponentRow): Component {
  return {
    slug: row.slug,
    name: row.name,
    kind: row.kind,
    kindLabel: row.kind_label,
    status: row.status,
    summary: row.summary,
    description: str(row.description),
    maintainer: row.maintainer,
    model: row.model,
    repo: row.repo,
    path: row.path,
    docUrl: row.doc_url,
    invocation: readInvocation(row.invocation),
    touches: TOUCHES.map((touch) => ({
      ...touch,
      entries: arr(row[touch.key]).map(toInteraction),
    })),
    secrets: readSecrets(row.secrets),
    extra: factsFrom(row.extra),
    recordsRuns: row.records_runs,
    recordsRunsNote: row.records_runs
      ? 'Records a run row, so it appears in Lanes and its cost reaches Costs.'
      : 'Records no run row, so it never appears in Lanes and contributes nothing to Costs.',
  }
}

/** kind_sort, then sort_order, then name. All three come from the row. */
function compare(a: FleetComponentRow, b: FleetComponentRow): number {
  return a.kind_sort - b.kind_sort || a.sort_order - b.sort_order || a.name.localeCompare(b.name)
}

/**
 * Every row, grouped by `kind` and ordered as the rows ask to be.
 *
 * The groups come out in `kind_sort` order because the rows were sorted first
 * and a Map keeps insertion order, so an unseen kind lands in its own place
 * under its own heading. A group's heading is the `kind_label` of its
 * first-sorting row: the rows of one kind agree on it today, and if they ever
 * disagree, showing one of theirs beats showing one of ours.
 */
export function groupComponents(rows: ReadonlyArray<FleetComponentRow>): ComponentGroup[] {
  const groups = new Map<string, ComponentGroup>()
  for (const row of [...rows].sort(compare)) {
    const group = groups.get(row.kind)
    if (group) group.components.push(toComponent(row))
    else groups.set(row.kind, { kind: row.kind, label: row.kind_label, components: [toComponent(row)] })
  }
  return [...groups.values()]
}

/**
 * When the directory was last published, and from what.
 *
 * The publisher replaces the whole set in one transaction, so the rows agree;
 * the newest `generated_at` is read rather than assumed to be shared, and its
 * own row's commit goes with it. Each part is stated only if it is there --
 * a page claiming a freshness it cannot support is the failure this is for.
 */
export function freshnessOf(rows: ReadonlyArray<FleetComponentRow>, now: number = Date.now()): Freshness {
  let newest: FleetComponentRow | null = null
  for (const row of rows) {
    const at = row.generated_at ? Date.parse(row.generated_at) : NaN
    if (Number.isNaN(at)) continue
    const best = newest?.generated_at ? Date.parse(newest.generated_at) : NaN
    if (Number.isNaN(best) || at > best) newest = row
  }
  const generatedAt = newest?.generated_at ?? null
  const commit = newest?.source_commit ? newest.source_commit.slice(0, 7) : null

  const sentence =
    generatedAt && commit ? `Checked ${age(generatedAt, now)}, from commit ${commit}.`
    : generatedAt ? `Checked ${age(generatedAt, now)}.`
    : commit ? `From commit ${commit}. It does not say when it was checked.`
    : 'This page does not say when it was last published.'

  return { sentence, generatedAt, commit }
}
